/**
 * One exception, and the decisions that can be taken on it.
 *
 * A panel from the right over the list, so the reader keeps their place.
 * A reason is required for every decision except Reopen. No internal ids:
 * the exception is named by what was found and the analysis that found it.
 */

import { useModule } from '../modules/useModule';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
	ConfirmDialog,
	ErrorNotice,
	Note,
	SideDrawer,
	StatusBadge,
} from '../../shared/ui';
import { ApiError } from '../../api/errors';
import { useToast } from '../../provider/toast';
import {
	useExceptionEventsQuery,
	useUpdateExceptionMutation,
} from '../../queries/exceptions';
import { useAuth, useTerm } from '../../provider/auth';
import { count, dateTime, humanise } from '../../utils/format';
import { Money, SeverityBadge } from '../shared/badges';
import type { AuditException, ExceptionStatus } from '../../schemas';
import { Button } from '@mui/material';

/** The vocabulary's tones, mapped onto `Note`'s own tone set — a different
 *  vocabulary than `StatusBadge`'s (`ui.tsx`'s `TONE_TO_BADGE`), because a
 *  `Note` has no "busy" or "neutral" look of its own, only `info`/`gray`. */
const TONE_TO_NOTE: Record<string, 'ok' | 'warn' | 'danger' | 'info' | 'gray'> =
	{
		good: 'ok',
		warn: 'warn',
		bad: 'danger',
		busy: 'info',
		neutral: 'gray',
	};

/** One action button below the drawer: the status it sets, its label, and
 *  whether setting it needs a written reason — mirrors the server's own
 *  rule (`exceptionService.updateException`: anything but OPEN needs one). */
interface StatusAction {
	status: ExceptionStatus;
	label: string;
	primary?: boolean;
	danger?: boolean;
}

function actionsFor(status: ExceptionStatus): StatusAction[] {
	if (status === 'OPEN') {
		return [
			{ status: 'ACCEPTED', label: 'Accept', primary: true },
			{ status: 'CORRECTED', label: 'Mark corrected' },
			{ status: 'ESCALATED', label: 'Escalate', danger: true },
		];
	}
	const actions: StatusAction[] = [{ status: 'OPEN', label: 'Reopen' }];
	if (status !== 'CLOSED') actions.push({ status: 'CLOSED', label: 'Close' });
	return actions;
}

const QUESTION: Partial<Record<ExceptionStatus, string>> = {
	ACCEPTED:
		'Accepting means the finding stands and the audit is content with it. The reason is written to the permanent record.',
	CORRECTED:
		'Marking this corrected means the client re-sent the data and the discrepancy is gone. If the same problem recurs on a later run it raises again as a new exception, not this one reopening.',
	ESCALATED:
		'Escalating flags this for a reviewer before sign-off. The reason is what they read first.',
	CLOSED: 'Closing removes this from the open queue without accepting or correcting it. The reason is what a later reviewer reads instead of asking again.',
};

export function ExceptionDetailsDrawer({
	exception,
	onClose,
	onChanged,
}: {
	exception: AuditException | null;
	onClose: () => void;
	onChanged: () => void;
}) {
	const [pending, setPending] = useState<StatusAction | null>(null);
	const { link } = useModule();
	const { vocabulary } = useAuth();
	const severityTerm = useTerm('severity', exception?.severity);
	const statusTerm = useTerm('exceptionStatus', exception?.status);

	const events = useExceptionEventsQuery(
		exception?.pkAuditExceptionId ?? null,
	);

	const toast = useToast();
	const updateMutation = useUpdateExceptionMutation();
	const update = {
		isPending: updateMutation.isPending,
		error: updateMutation.error,
		run: (id: string, status: ExceptionStatus, note: string) =>
			updateMutation.mutate(
				{ id, status, note },
				{
					onSuccess: () => {
						setPending(null);
						toast('ok', 'Exception updated.');
						onChanged();
					},
				},
			),
	};

	if (!exception) return null;

	/* ── ONE STATUS, NOT TWO ───────────────────────────────────────────────
     `severity`/`isBlocking` never change once an exception is raised
     (raising, before it was removed, deliberately never updated them) —
     they classify the FINDING, not its current state. `status` is the one
     thing that changes when a human decides. Feedback round 2026-09-14,
     T7: an auditor accepted an exception and the drawer kept showing a
     "Blocking" badge/note beside "Accepted" — both true, but read as two
     contradicting statuses, and the note's own copy ("the report cannot be
     issued while this stands") is server vocabulary written for the OPEN
     case, so it went on asserting a live block that had already been lifted.
     `blocksReport` is exactly auditService's own definition of what counts
     against the report (`status === 'OPEN' && isBlocking`) — once false,
     nothing on screen claims the report is still blocked by this row. */
	const blocksReport = exception.isBlocking && exception.status === 'OPEN';

	return (
		<>
			<SideDrawer
				open
				onClose={onClose}
				title={exception.title}
				subtitle={exception.detail}
				footer={
					<div className="spread">
						<Button variant="outlined" onClick={onClose}>
							Close
						</Button>
						<div
							className="row"
							style={{ gap: 'var(--s2)', flexWrap: 'wrap' }}
						>
							{actionsFor(exception.status).map((action) => (
								<Button
									key={action.status}
									size="small"
									variant={
										action.primary
											? 'contained'
											: 'outlined'
									}
									color={action.danger ? 'error' : 'primary'}
									disabled={update.isPending}
									onClick={() => {
										if (action.status === 'OPEN') {
											update.run(
												exception.pkAuditExceptionId,
												'OPEN',
												'',
											);
										} else {
											setPending(action);
										}
									}}
								>
									{action.label}
								</Button>
							))}
						</div>
					</div>
				}
			>
				<div className="stack">
					<ErrorNotice error={update.error} />

					<div
						className="row"
						style={{
							gap: 'var(--s2)',
							flexWrap: 'wrap',
							alignItems: 'center',
						}}
					>
						{/* Severity is only shown alongside status while OPEN: before a
                decision exists, "how serious" and "what's been decided" are
                both live questions and neither contradicts the other. Once
                resolved, status is the one status a person reads — severity
                becomes a fact about the finding, stated below instead. */}
						{exception.status === 'OPEN' && (
							<SeverityBadge value={exception.severity} />
						)}
						<StatusBadge
							group="exceptionStatus"
							value={exception.status}
						/>
					</div>

					{exception.status === 'OPEN' && (
						<Note
							tone={TONE_TO_NOTE[severityTerm.tone ?? 'neutral']}
							title={severityTerm.label}
						>
							{severityTerm.help}
						</Note>
					)}

					<div>
						<p className="small b">What was found</p>
						<p className="small">{exception.detail}</p>
						{/* Blocking is a property of the finding, not a live claim — say
                so in the past tense once it's no longer true, rather than
                letting the badge above stand for it unexplained. */}
						{exception.status !== 'OPEN' &&
							exception.isBlocking && (
								<p className="xs mut">
									Raised as a blocking finding —{' '}
									{statusTerm.label.toLowerCase()}, so it no
									longer blocks the report.
								</p>
							)}
					</div>

					<dl className="kv">
						<dt>Rows affected</dt>
						<dd>{count(exception.rowsAffected)}</dd>
						<dt>Value</dt>
						<dd>
							<Money value={exception.valueAffected} />
						</dd>
						<dt>Analysis</dt>
						<dd>
							<Link
								to={link(
									`/audits/${exception.fkAuditId}/analyses/${exception.analysisSlug}`,
								)}
								className="link"
								onClick={onClose}
							>
								Open the result
							</Link>
						</dd>
						<dt>Raised</dt>
						<dd>{dateTime(exception.createdAt)}</dd>
						{exception.resolvedAt && (
							<>
								<dt>Last decided</dt>
								<dd>{dateTime(exception.resolvedAt)}</dd>
							</>
						)}
					</dl>

					{exception.status !== 'OPEN' &&
						exception.resolutionNote && (
							<Note
								tone="ok"
								title={`${statusTerm.label} — reason on the record`}
							>
								<p style={{ margin: 0 }}>
									{exception.resolutionNote}
								</p>
								<p
									className="xs mut"
									style={{ marginBottom: 0 }}
								>
									{exception.resolvedAt
										? dateTime(exception.resolvedAt)
										: null}
								</p>
							</Note>
						)}

					<div>
						<p className="small b">Activity</p>
						<ErrorNotice error={events.error} />
						{events.isPending ? (
							<p className="small mut">Reading the history…</p>
						) : (events.data ?? []).length === 0 ? (
							<p className="small mut">
								No decisions recorded yet.
							</p>
						) : (
							<div className="feed">
								{[...(events.data ?? [])]
									.reverse()
									.map((entry) => (
										<div key={entry.pkExceptionEventId}>
											<div className="small b">
												{vocabulary?.exceptionStatus?.[
													entry.action
												]?.label ??
													humanise(entry.action)}
											</div>
											{entry.note && (
												<div className="small mut">
													{entry.note}
												</div>
											)}
											<div className="small mut">
												{entry.actorName ??
													'Reconcilio'}{' '}
												· {dateTime(entry.createdAt)}
											</div>
										</div>
									))}
							</div>
						)}
					</div>
				</div>
			</SideDrawer>

			<ConfirmDialog
				open={pending !== null}
				onClose={() => setPending(null)}
				onConfirm={(reason) =>
					pending &&
					update.run(
						exception.pkAuditExceptionId,
						pending.status,
						reason,
					)
				}
				title={pending ? `${pending.label}: ${exception.title}` : ''}
				confirmLabel={pending?.label ?? 'Confirm'}
				reasonRequired
				danger={pending?.danger}
				question={pending ? QUESTION[pending.status] : undefined}
				impact={
					blocksReport && pending?.status !== 'OPEN' ? (
						<>
							This is a blocking exception: while it is open the
							report cannot be issued. Deciding it removes that
							block from the report's checklist.
						</>
					) : undefined
				}
				pending={update.isPending}
				error={update.error instanceof ApiError ? update.error : null}
			/>
		</>
	);
}
